import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Pencil, Plus, RefreshCw, Sparkles, Trash2, Users, X } from "lucide-react";
import {
  AUDIENCES,
  ADDRESS_CHANNELS,
  OPS_BY_TYPE,
  OP_LABELS,
  VALUELESS_OPS,
  audienceLabel,
  errorMessage,
  formatNumber,
  formatRelative,
  groupFields,
  inputToRuleValue,
  ruleValueToInput,
  type Audience,
  type Channel,
  type MatchMode,
  type RuleOp,
  type Segment,
  type SegmentField,
  type SegmentPreset,
  type SegmentPreview,
  type SegmentRule,
  type SegmentsResponse,
} from "@/lib/comms";
import { EmptyState, ErrorState, TableSkeleton } from "./shared";

/** A rule while it is being edited: the value stays a string until it is submitted. */
interface DraftRule {
  field: string;
  op: RuleOp;
  value: string;
}

interface Draft {
  id: string | null;
  name: string;
  description: string;
  audience: Audience;
  match: MatchMode;
  rules: DraftRule[];
}

const EMPTY_DRAFT: Draft = {
  id: null,
  name: "",
  description: "",
  audience: "parents",
  match: "all",
  rules: [],
};

function toDraftRules(rules: SegmentRule[] | null | undefined): DraftRule[] {
  return (rules || []).map(r => ({ field: r.field, op: r.op, value: ruleValueToInput(r.value) }));
}

function fieldType(fields: SegmentField[], id: string) {
  return fields.find(f => f.id === id)?.type || "text";
}

function ruleSummary(rule: SegmentRule, fields: SegmentField[]): string {
  const label = fields.find(f => f.id === rule.field)?.label || rule.field;
  const op = OP_LABELS[rule.op] || rule.op;
  if (VALUELESS_OPS.includes(rule.op)) return `${label} ${op}`;
  return `${label} ${op} ${ruleValueToInput(rule.value)}`;
}

function RuleRow({
  rule,
  fields,
  onChange,
  onRemove,
  index,
}: {
  rule: DraftRule;
  fields: SegmentField[];
  onChange: (next: DraftRule) => void;
  onRemove: () => void;
  index: number;
}) {
  const type = fieldType(fields, rule.field);
  const ops = OPS_BY_TYPE[type];
  const grouped = useMemo(() => groupFields(fields), [fields]);
  const help = fields.find(f => f.id === rule.field)?.help;
  const needsValue = !VALUELESS_OPS.includes(rule.op);

  return (
    <div className="space-y-1">
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1.2fr)_auto] items-center">
        <Select
          value={rule.field}
          onValueChange={v => {
            const nextType = fieldType(fields, v);
            const nextOps = OPS_BY_TYPE[nextType];
            const op = nextOps.includes(rule.op) ? rule.op : nextOps[0];
            onChange({ field: v, op, value: nextType === "bool" ? "true" : "" });
          }}
        >
          <SelectTrigger aria-label={`Rule ${index + 1} field`} data-testid={`select-rule-field-${index}`}>
            <SelectValue placeholder="Choose a field" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {grouped.map(g => (
              <SelectGroup key={g.group}>
                <SelectLabel>{g.group}</SelectLabel>
                {g.fields.map(f => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={rule.op}
          onValueChange={v => {
            const op = v as RuleOp;
            onChange({ ...rule, op, value: VALUELESS_OPS.includes(op) ? "" : rule.value });
          }}
        >
          <SelectTrigger aria-label={`Rule ${index + 1} operator`} data-testid={`select-rule-op-${index}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ops.map(op => <SelectItem key={op} value={op}>{OP_LABELS[op]}</SelectItem>)}
          </SelectContent>
        </Select>

        {!needsValue ? (
          <span className="text-sm text-muted-foreground px-1">No value needed</span>
        ) : type === "bool" ? (
          <Select value={rule.value || "true"} onValueChange={v => onChange({ ...rule, value: v })}>
            <SelectTrigger aria-label={`Rule ${index + 1} value`} data-testid={`select-rule-value-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Yes</SelectItem>
              <SelectItem value="false">No</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <Input
            value={rule.value}
            onChange={e => onChange({ ...rule, value: e.target.value })}
            type={type === "number" && rule.op !== "in" ? "number" : "text"}
            placeholder={rule.op === "in" ? "Comma separated" : type === "number" ? "0" : "Value"}
            aria-label={`Rule ${index + 1} value`}
            data-testid={`input-rule-value-${index}`}
          />
        )}

        <Button size="sm" variant="ghost" type="button" onClick={onRemove} title="Remove rule" data-testid={`button-remove-rule-${index}`}>
          <X className="w-4 h-4" />
        </Button>
      </div>
      {help ? <p className="text-xs text-muted-foreground">{help}</p> : null}
    </div>
  );
}

function PreviewPanel({
  draft,
  fields,
  channel,
  onChannelChange,
}: {
  draft: Draft;
  fields: SegmentField[];
  channel: Channel;
  onChannelChange: (c: Channel) => void;
}) {
  const [debounced, setDebounced] = useState<string>("");

  const payload = useMemo(() => {
    const rules = draft.rules
      .filter(r => r.field && (VALUELESS_OPS.includes(r.op) || r.value !== ""))
      .map(r => {
        const value = inputToRuleValue(r.value, fieldType(fields, r.field), r.op);
        return value === undefined ? { field: r.field, op: r.op } : { field: r.field, op: r.op, value };
      });
    return { rules, match: draft.match, audience: draft.audience, channel };
  }, [draft.rules, draft.match, draft.audience, channel, fields]);

  const key = JSON.stringify(payload);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(key), 400);
    return () => clearTimeout(t);
  }, [key]);

  const { data, isFetching, error } = useQuery<SegmentPreview>({
    queryKey: ["/api/admin/comms/segments/preview", debounced],
    queryFn: async () => (await apiRequest("POST", "/api/admin/comms/segments/preview", JSON.parse(debounced))).json(),
    enabled: !!debounced,
  });

  return (
    <div className="border rounded-md p-3 space-y-3 bg-muted/30">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium flex items-center gap-2">
          <Users className="w-4 h-4" /> Live preview
        </p>
        <Select value={channel} onValueChange={v => onChannelChange(v as Channel)}>
          <SelectTrigger className="w-[140px] h-8" aria-label="Preview channel"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ADDRESS_CHANNELS.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {error ? (
        <p className="text-sm text-destructive">{(error as Error).message}</p>
      ) : !debounced || (isFetching && !data) ? (
        <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Counting…</p>
      ) : (
        <>
          <p className="text-2xl font-bold" data-testid="text-preview-count">
            {formatNumber(data?.count)}{" "}
            <span className="text-sm font-normal text-muted-foreground">
              reachable by {ADDRESS_CHANNELS.find(c => c.id === channel)?.label.toLowerCase()}
              {isFetching ? " · updating" : ""}
            </span>
          </p>
          {!data?.sample || data.sample.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nobody matches yet. Loosen a rule, or switch the match mode to any.
            </p>
          ) : (
            <div className="max-h-56 overflow-y-auto rounded-md border bg-background">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Address</TableHead>
                    <TableHead>Who</TableHead>
                    <TableHead>Grade</TableHead>
                    <TableHead>Weakest topic</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.sample.slice(0, 20).map((s, i) => (
                    <TableRow key={`${s.address}-${i}`}>
                      <TableCell className="text-xs break-all">{s.address}</TableCell>
                      <TableCell className="text-xs">
                        {s.firstName || "Unknown"}
                        {s.childFirstName ? <span className="text-muted-foreground"> · child {s.childFirstName}</span> : null}
                      </TableCell>
                      <TableCell className="text-xs">{s.grade ?? "n/a"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{s.weakestTopic || "n/a"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function SegmentsTab() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [previewChannel, setPreviewChannel] = useState<Channel>("email");
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Segment | null>(null);
  const [countingId, setCountingId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<SegmentsResponse>({ queryKey: ["/api/admin/comms/segments"] });
  const fields = data?.fields || [];
  const presets = data?.presets || [];
  const segments = data?.segments || [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/segments"] });

  const saveMutation = useMutation({
    mutationFn: async (payload: { id: string | null; body: Record<string, unknown> }) => {
      const res = payload.id
        ? await apiRequest("PATCH", `/api/admin/comms/segments/${payload.id}`, payload.body)
        : await apiRequest("POST", "/api/admin/comms/segments", payload.body);
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      toast({ title: draft.id ? "Segment updated" : "Segment created" });
    },
    onError: (e: unknown) => {
      setFormError(errorMessage(e));
      toast({ title: "Could not save the segment", description: errorMessage(e), variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await apiRequest("DELETE", `/api/admin/comms/segments/${id}`)).json(),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
      toast({ title: "Segment deleted" });
    },
    onError: (e: unknown) => toast({ title: "Could not delete the segment", description: errorMessage(e), variant: "destructive" }),
  });

  const countMutation = useMutation({
    mutationFn: async (segment: Segment) =>
      (await apiRequest("POST", `/api/admin/comms/segments/${segment.id}/count?channel=email`)).json() as Promise<{ count: number }>,
    onSuccess: (res: { count: number }) => {
      invalidate();
      setCountingId(null);
      toast({ title: "Recounted", description: `${formatNumber(res.count)} contacts match right now.` });
    },
    onError: (e: unknown) => {
      setCountingId(null);
      toast({ title: "Could not recount", description: errorMessage(e), variant: "destructive" });
    },
  });

  const openCreate = () => {
    setDraft(EMPTY_DRAFT);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (segment: Segment) => {
    setDraft({
      id: segment.id,
      name: segment.name,
      description: segment.description || "",
      audience: segment.audience,
      match: segment.match,
      rules: toDraftRules(segment.rules),
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const applyPreset = (preset: SegmentPreset) => {
    setDraft({
      id: null,
      name: preset.name,
      description: preset.description || "",
      audience: preset.audience,
      match: preset.match,
      rules: toDraftRules(preset.rules),
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const addRule = () => {
    const first = fields[0];
    if (!first) return;
    const op = OPS_BY_TYPE[first.type][0];
    setDraft(d => ({ ...d, rules: [...d.rules, { field: first.id, op, value: first.type === "bool" ? "true" : "" }] }));
  };

  const submit = () => {
    setFormError(null);
    if (!draft.name.trim()) {
      setFormError("Give the segment a name so it can be picked in a campaign.");
      return;
    }
    const incomplete = draft.rules.find(r => !r.field || (!VALUELESS_OPS.includes(r.op) && r.value.trim() === ""));
    if (incomplete) {
      setFormError("Every rule needs a field and a value. Remove the empty row or fill it in.");
      return;
    }
    const rules = draft.rules.map(r => {
      const value = inputToRuleValue(r.value, fieldType(fields, r.field), r.op);
      return value === undefined ? { field: r.field, op: r.op } : { field: r.field, op: r.op, value };
    });
    saveMutation.mutate({
      id: draft.id,
      body: {
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        audience: draft.audience,
        match: draft.match,
        rules,
      },
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-base">Segments</CardTitle>
              <CardDescription>
                A segment is a saved question about the contact book, answered fresh at launch. Counts below are the last time the segment was
                measured, not a live number.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" disabled={presets.length === 0} data-testid="button-presets">
                    <Sparkles className="w-4 h-4 mr-2" /> Start from a preset
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <DropdownMenuLabel>Presets</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {presets.map(p => (
                    <DropdownMenuItem key={p.name} onSelect={() => applyPreset(p)} className="flex flex-col items-start gap-0.5">
                      <span className="font-medium">{p.name}</span>
                      <span className="text-xs text-muted-foreground whitespace-normal">{p.description}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button onClick={openCreate} data-testid="button-new-segment">
                <Plus className="w-4 h-4 mr-2" /> New segment
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <ErrorState error={error} />
          ) : isLoading ? (
            <TableSkeleton rows={5} cols={5} />
          ) : segments.length === 0 ? (
            <EmptyState
              title="No segments yet"
              hint="Nothing has been saved. Start from a preset for the common ones, or build a segment from scratch."
              action={<Button size="sm" onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> New segment</Button>}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Audience</TableHead>
                    <TableHead>Rules</TableHead>
                    <TableHead>Last count</TableHead>
                    <TableHead className="w-32" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {segments.map(s => (
                    <TableRow key={s.id} data-testid={`row-segment-${s.id}`}>
                      <TableCell>
                        <p className="font-medium">{s.name}</p>
                        {s.description ? <p className="text-xs text-muted-foreground">{s.description}</p> : null}
                      </TableCell>
                      <TableCell><Badge variant="outline">{audienceLabel(s.audience)}</Badge></TableCell>
                      <TableCell className="max-w-sm">
                        {(s.rules || []).length === 0 ? (
                          <span className="text-sm text-muted-foreground">Everyone in the audience</span>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            Match {s.match}: {(s.rules || []).map(r => ruleSummary(r, fields)).join(s.match === "all" ? " and " : " or ")}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="font-semibold">{s.lastCount === null || s.lastCount === undefined ? "Never counted" : formatNumber(s.lastCount)}</p>
                        {s.lastCountAt ? <p className="text-xs text-muted-foreground">{formatRelative(s.lastCountAt)}</p> : null}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Recount now"
                            onClick={() => { setCountingId(s.id); countMutation.mutate(s); }}
                            disabled={countMutation.isPending && countingId === s.id}
                            data-testid={`button-recount-${s.id}`}
                          >
                            {countMutation.isPending && countingId === s.id
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <RefreshCw className="w-4 h-4" />}
                          </Button>
                          <Button size="sm" variant="ghost" title="Edit segment" onClick={() => openEdit(s)} data-testid={`button-edit-segment-${s.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" title="Delete segment" onClick={() => setDeleteTarget(s)} data-testid={`button-delete-segment-${s.id}`}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Builder */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{draft.id ? "Edit segment" : "New segment"}</DialogTitle>
            <DialogDescription>
              Rules read the per-child facts rebuilt by Refresh on the Overview tab. The preview counts contacts that are reachable on the chosen
              channel, so it is smaller than the raw match.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="segment-name">Name</Label>
                <Input
                  id="segment-name"
                  value={draft.name}
                  onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                  placeholder="e.g. Grade 6 parents, slipping"
                  data-testid="input-segment-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="segment-audience">Audience</Label>
                <Select value={draft.audience} onValueChange={v => setDraft(d => ({ ...d, audience: v as Audience }))}>
                  <SelectTrigger id="segment-audience"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AUDIENCES.map(a => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="segment-description">Description</Label>
              <Textarea
                id="segment-description"
                value={draft.description}
                onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
                placeholder="What this group is, in one line, so the next person knows when to use it."
                rows={2}
                data-testid="textarea-segment-description"
              />
            </div>

            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label>Rules</Label>
                <div className="flex items-center gap-2">
                  <Select value={draft.match} onValueChange={v => setDraft(d => ({ ...d, match: v as MatchMode }))}>
                    <SelectTrigger className="w-[190px] h-8" aria-label="Match mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Match all rules</SelectItem>
                      <SelectItem value="any">Match any rule</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" type="button" onClick={addRule} disabled={fields.length === 0} data-testid="button-add-rule">
                    <Plus className="w-4 h-4 mr-2" /> Add rule
                  </Button>
                </div>
              </div>

              {draft.rules.length === 0 ? (
                <p className="text-sm text-muted-foreground border rounded-md p-3">
                  No rules. The segment is everyone in the chosen audience who is reachable on the campaign's channel.
                </p>
              ) : (
                <div className="space-y-3">
                  {draft.rules.map((r, i) => (
                    <RuleRow
                      key={i}
                      index={i}
                      rule={r}
                      fields={fields}
                      onChange={next => setDraft(d => ({ ...d, rules: d.rules.map((x, xi) => (xi === i ? next : x)) }))}
                      onRemove={() => setDraft(d => ({ ...d, rules: d.rules.filter((_, xi) => xi !== i) }))}
                    />
                  ))}
                </div>
              )}
            </div>

            <PreviewPanel draft={draft} fields={fields} channel={previewChannel} onChannelChange={setPreviewChannel} />

            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saveMutation.isPending} data-testid="button-save-segment">
              {saveMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : draft.id ? "Save changes" : "Create segment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={o => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this segment?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.name} will be gone. Campaigns still pointing at it will not be able to launch until another segment is picked. This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              data-testid="button-confirm-delete-segment"
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
